import { Box, Table, TableContainer } from '@shared/components';
import { TableBody, TableHeader } from '@shared/table-generator';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import type { AppRolePermissionForm, AppPermissionDetails } from '../../types';
import { mapAppRolePermissionToForm } from '../../utils';

import type { PermissionListColumn } from './columns';
import { generateRows } from './rows';

interface PermissionListProps {
  appPermissionDetails: AppPermissionDetails[];
  columns: PermissionListColumn[];
}

export default function PermissionList(props: Readonly<PermissionListProps>) {
  const { appPermissionDetails, columns } = props;

  const formContext = useFormContext<AppRolePermissionForm>();

  useEffect(() => {
    const formData = mapAppRolePermissionToForm(appPermissionDetails);
    formContext.reset(formData);
  }, [appPermissionDetails]);

  const rows = useMemo(
    () => generateRows(appPermissionDetails, columns),
    [appPermissionDetails, columns],
  );

  return (
    <Box>
      <TableContainer
        sx={(theme) => ({
          borderRadius: theme.shape.rounded(2),
          border: `1px solid ${theme.palette.grey[300]}`,
        })}
      >
        <Table>
          <TableHeader
            columns={columns}
            slotProps={{
              tableHeaderCell: {
                sx: (theme) => ({
                  bgcolor: theme.palette.common.black,
                  color: theme.palette.common.white,
                  p: theme.spacing(3),
                  '&:not(:first-of-type)': {
                    borderLeft: `1px solid ${theme.palette.grey[300]}`,
                  },
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                  ...theme.typography.body1Semibold,
                }),
              },
            }}
          />
          <TableBody
            rows={rows}
            slotProps={{
              tableBodyCell: {
                sx: (theme) => ({
                  p: theme.spacing(3),
                  height: '48px',
                  lineHeight: 0,
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                  '&:not(:last-child)': {
                    borderRight: `1px solid ${theme.palette.grey[300]}`,
                  },
                }),
              },
              tableBodyRow: {
                sx: {
                  '&:last-child': {
                    '& > td': {
                      borderBottom: 'none',
                    },
                  },
                },
              },
            }}
          />
        </Table>
      </TableContainer>
    </Box>
  );
}
